/**
 * QR payload for a Short_Code: `https://{host}/c/{encodeURIComponent(code)}`.
 *
 * Platform-neutral: uses only the WHATWG `URL` global.
 *
 * Requirement 11.7.
 */
import { parse } from './shortcode.js';
/** Path prefix of a product-code QR URL. */
export const QR_PATH_PREFIX = '/c/';
/**
 * Canonicalise a configured host (`example.com`, `Example.COM:8443`) to the form
 * `URL#host` reports. Throws on anything that is not a bare host[:port].
 */
function canonicalHost(host) {
    let u;
    try {
        u = new URL(`https://${host}`);
    }
    catch {
        throw new TypeError(`Invalid QR host: ${host}`);
    }
    if (u.host === '' ||
        u.username !== '' ||
        u.password !== '' ||
        u.pathname !== '/' ||
        u.search !== '' ||
        u.hash !== '' ||
        // Reject inputs like "a.com/x" or "a.com?x" that URL would silently split.
        /[/?#@\\]/.test(host)) {
        throw new TypeError(`Invalid QR host: ${host}`);
    }
    return u.host;
}
/**
 * Build the QR URL for a Short_Code. The code is parsed first, so any accepted
 * input form is emitted in its normalised 12-symbol form. Check symbols that are
 * not URL-safe (`$`, `=`) are percent-encoded; `*` and `~` are left as-is, which
 * `encodeURIComponent` also does.
 */
export function toQrUrl(code, host) {
    const parsed = parse(code);
    if (!parsed.ok)
        throw new TypeError(`Invalid Short_Code (${parsed.error})`);
    return `https://${canonicalHost(host)}${QR_PATH_PREFIX}${encodeURIComponent(parsed.code)}`;
}
/**
 * Parse a scanned QR URL. Accepts only `https:` URLs whose host equals `host`
 * (case-insensitive, default port ignored) and whose path is exactly
 * `/c/{one segment}` with no query or fragment; the segment is percent-decoded
 * and validated with `parse`. Purely local: no repository lookup.
 */
export function parseQr(url, host) {
    const expectedHost = canonicalHost(host);
    let u;
    try {
        u = new URL(url);
    }
    catch {
        return { ok: false, error: 'url' };
    }
    if (u.protocol !== 'https:' ||
        u.host !== expectedHost ||
        u.username !== '' ||
        u.password !== '' ||
        u.search !== '' ||
        u.hash !== '' ||
        // URL drops an empty "?" / "#"; reject those too.
        /[?#]/.test(url) ||
        !u.pathname.startsWith(QR_PATH_PREFIX)) {
        return { ok: false, error: 'url' };
    }
    const segment = u.pathname.slice(QR_PATH_PREFIX.length);
    if (segment === '' || segment.includes('/'))
        return { ok: false, error: 'url' };
    let decoded;
    try {
        decoded = decodeURIComponent(segment);
    }
    catch {
        return { ok: false, error: 'url' };
    }
    return parse(decoded);
}
//# sourceMappingURL=qr.js.map